import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, errMsg, fmtDate, money, imgUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/RequireAuth";
import { useAuth } from "@/context/AuthContext";
import StatusBadge, { bookingState } from "@/components/StatusBadge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TaxRules } from "@/components/TaxRules";
import { TaxZoneReview, TaxComplianceNotice } from "@/components/AdminTaxZone";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { BookingsSwitch } from "@/components/ComingSoon";
import { AdminLedger, AdminCancelBooking } from "@/components/AdminLedger";
import { AdminTaxLiability, FilingBanner } from "@/components/AdminTaxLiability";
import { AdminIncome } from "@/components/AdminIncome";

const Metric = ({ label, value, testid, accent }) => (
  <div className={`rounded-3xl border p-5 ${accent || "bg-white"}`}>
    <p className="text-xs font-semibold uppercase tracking-wider text-stone-500">{label}</p>
    <p className="mt-2 font-mono-num text-2xl font-semibold" data-testid={testid}>{value}</p>
  </div>
);

function Users() {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const { data = [] } = useQuery({ queryKey: ["admin-users"], queryFn: () => api.get("/admin/users").then((r) => r.data) });
  const toggle = async (u, v) => {
    try {
      await api.post(`/admin/users/${u.user_id}/suspend`, { suspended: v });
      toast.success(v ? `${u.name} suspended` : `${u.name} reinstated`);
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Table data-testid="admin-users-table">
      <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Listings</TableHead><TableHead>Trips</TableHead><TableHead>Host Agreement</TableHead><TableHead>Suspended</TableHead></TableRow></TableHeader>
      <TableBody>
        {data.map((u) => (
          <TableRow key={u.user_id} data-testid={`admin-user-${u.user_id}`}>
            <TableCell><Link to={`/users/${u.user_id}`} className="font-medium hover:underline">{u.name}</Link></TableCell>
            <TableCell className="text-stone-600">{u.email}</TableCell>
            <TableCell>{u.user_id === me.user_id ? "Admin (you)" : u.is_host ? "Host" : "Guest"}{u.is_sample ? " · sample" : ""}</TableCell>
            <TableCell>{u.listing_count}</TableCell>
            <TableCell>{u.booking_count}</TableCell>
            <TableCell className="whitespace-nowrap text-xs text-stone-600" data-testid={`admin-user-agreement-${u.user_id}`}>{u.host_agreement_agreed_at ? `v${u.host_agreement_version} · ${fmtDate(u.host_agreement_agreed_at)}` : "—"}</TableCell>
            <TableCell>{u.user_id !== me.user_id && <Switch data-testid={`admin-suspend-${u.user_id}`} checked={!!u.suspended} onCheckedChange={(v) => toggle(u, v)} />}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function Bookings() {
  const { data = [] } = useQuery({ queryKey: ["admin-bookings"], queryFn: () => api.get("/admin/bookings").then((r) => r.data) });
  return (
    <Table data-testid="admin-bookings-table">
      <TableHeader><TableRow><TableHead>Listing</TableHead><TableHead>Guest</TableHead><TableHead>Dates</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Total</TableHead><TableHead className="text-right">Commission</TableHead><TableHead /></TableRow></TableHeader>
      <TableBody>
        {data.map((b) => (
          <TableRow key={b.booking_id} data-testid={`admin-booking-${b.booking_id}`}>
            <TableCell className="max-w-[220px] truncate font-medium">{b.is_test && <span className="mr-1 rounded bg-amber-100 px-1.5 text-xs font-bold text-amber-800" data-testid={`admin-booking-test-${b.booking_id}`}>TEST</span>}{b.listing.title}</TableCell>
            <TableCell>{b.guest?.name}</TableCell>
            <TableCell className="whitespace-nowrap text-stone-600">{fmtDate(b.check_in)} → {fmtDate(b.check_out)}</TableCell>
            <TableCell><StatusBadge status={bookingState(b)} />{b.decline_reason && <p className="mt-1 text-xs text-stone-500" data-testid={`admin-decline-reason-${b.booking_id}`}>Reason: {b.decline_reason}</p>}</TableCell>
            <TableCell className="text-right font-mono-num">{money(b.total)}</TableCell>
            <TableCell className="text-right font-mono-num">{money(b.commission)}</TableCell>
            <TableCell><AdminCancelBooking b={b} />{b.admin_cancellation && <p className="text-xs text-red-700" data-testid={`admin-cancelled-by-${b.booking_id}`}>FairStay cancelled: {b.admin_cancellation.reason}</p>}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function Approvals() {
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["admin-listings"], queryFn: () => api.get("/admin/listings").then((r) => r.data) });
  const [notes, setNotes] = useState({});
  const review = async (l, decision) => {
    try {
      await api.post(`/admin/listings/${l.listing_id}/review`, { decision, note: notes[l.listing_id] || "" });
      toast.success(decision === "approved" ? "Listing approved and live" : "Sent back to host");
      qc.invalidateQueries({ queryKey: ["admin-listings"] });
      qc.invalidateQueries({ queryKey: ["admin-overview"] });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-3" data-testid="admin-approvals">
      {data.map((l) => (
        <div key={l.listing_id} className="grid min-w-0 gap-4 rounded-2xl border bg-white p-4 md:grid-cols-[minmax(0,1fr)_auto]" data-testid={`admin-approval-${l.listing_id}`}>
          <div className="min-w-0 space-y-1 break-words text-sm">
            <div className="flex flex-wrap items-center gap-2"><StatusBadge status={l.approval_status} /><Link to={`/listings/${l.listing_id}`} className="font-semibold hover:underline">{l.title}</Link></div>
            <p className="text-stone-600">{l.location} · host {l.host?.name}{l.is_sample ? " · sample" : ""}</p>
            <p>Permit no.: <span className="font-mono-num">{l.permit_number || "—"}</span> · Host confirmed it's allowed: {l.host_attestation ? "yes" : l.is_sample ? "n/a (sample)" : "no"}</p>
            {l.permit_document && <a href={imgUrl(l.permit_document)} target="_blank" rel="noreferrer" className="font-semibold text-primary hover:underline" data-testid={`admin-permit-doc-${l.listing_id}`}>View permit photo</a>}
            <p data-testid={`admin-insurance-${l.listing_id}`}>Insurance: {l.insurance_provider || "—"} · expires {l.insurance_expiry ? fmtDate(l.insurance_expiry) : "—"}
              {l.insurance_expiry && l.insurance_expiry <= new Date().toISOString().slice(0, 10) && <span className="ml-2 font-semibold text-red-700">EXPIRED</span>}</p>
            {l.insurance_document && <a href={imgUrl(l.insurance_document)} target="_blank" rel="noreferrer" className="font-semibold text-primary hover:underline" data-testid={`admin-insurance-doc-${l.listing_id}`}>View insurance proof</a>}
            <TaxZoneReview l={l} />
          </div>
          {l.approval_status !== "approved" ? (
            <div className="flex flex-col gap-2 md:w-72">
              <Input placeholder="Note to host (optional)" value={notes[l.listing_id] || ""} onChange={(e) => setNotes({ ...notes, [l.listing_id]: e.target.value })} data-testid={`admin-approval-note-${l.listing_id}`} />
              <div className="flex gap-2">
                <Button size="sm" className="flex-1 rounded-full" onClick={() => review(l, "approved")} data-testid={`admin-approve-${l.listing_id}`}>Approve</Button>
                <Button size="sm" variant="outline" className="flex-1 rounded-full" onClick={() => review(l, "rejected")} data-testid={`admin-reject-${l.listing_id}`}>Needs changes</Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-2 md:w-72">
              <Input placeholder="Reason for unpublishing (required)" value={notes[l.listing_id] || ""} onChange={(e) => setNotes({ ...notes, [l.listing_id]: e.target.value })} data-testid={`admin-unpublish-note-${l.listing_id}`} />
              <Button size="sm" variant="ghost" className="rounded-full" disabled={!notes[l.listing_id]?.trim()} onClick={() => review(l, "rejected")} data-testid={`admin-unpublish-${l.listing_id}`}>Unpublish</Button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function Complaints() {
  const { data = [] } = useQuery({ queryKey: ["admin-complaints"], queryFn: () => api.get("/admin/complaints").then((r) => r.data) });
  if (!data.length) return <p className="py-12 text-center text-stone-500">No complaints yet.</p>;
  return (
    <div className="space-y-3" data-testid="admin-complaints-queue">
      {data.map((c) => (
        <Link key={c.complaint_id} to={`/complaints/${c.complaint_id}`} className="flex flex-wrap items-center gap-3 rounded-2xl border bg-white p-4 transition-shadow hover:shadow-md" data-testid={`admin-complaint-${c.complaint_id}`}>
          <StatusBadge status={c.decision || c.status} />
          <span className="font-semibold">{c.category}</span>
          <span className="text-sm text-stone-500">{c.listing?.title} · {c.guest?.name} vs {c.host?.name} · {fmtDate(c.created_at)}</span>
          <span className="ml-auto text-sm text-stone-600">{c.evidence.length + (c.host_response?.evidence?.length || 0)} photos · {c.host_response ? "host replied" : "no host reply"}</span>
        </Link>
      ))}
    </div>
  );
}

export default function Admin() {
  const { data: o, isLoading } = useQuery({ queryKey: ["admin-overview"], queryFn: () => api.get("/admin/overview").then((r) => r.data) });
  if (isLoading) return <Spinner />;
  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <h1 className="mb-8 text-4xl font-bold tracking-tight">Admin</h1>
      <div className="mb-10 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6" data-testid="admin-overview">
        <Metric label="Commission earned" value={money(o.commission)} testid="admin-commission" accent="bg-emerald-50 border-emerald-200" />
        <Metric label="Paid (after refunds)" value={money(o.gross)} testid="admin-gross" />
        <Metric label="Refunds" value={money(o.refunds)} testid="admin-refunds" />
        <Metric label="Open complaints" value={o.open_complaints} testid="admin-open-complaints" />
        <Metric label="Bookings" value={`${o.paid_bookings}/${o.bookings}`} testid="admin-bookings-count" />
        <Metric label="Users · listings" value={`${o.users} · ${o.listings}`} testid="admin-users-count" />
      </div>
      <BookingsSwitch />
      <FilingBanner />
      <Tabs defaultValue="complaints">
        <TabsList className="mb-6 h-auto max-w-full flex-wrap justify-start rounded-3xl">
          <TabsTrigger value="complaints" className="rounded-full" data-testid="admin-tab-complaints">Complaints</TabsTrigger>
          <TabsTrigger value="approvals" className="rounded-full" data-testid="admin-tab-approvals">Listing approvals</TabsTrigger>
          <TabsTrigger value="bookings" className="rounded-full" data-testid="admin-tab-bookings">Bookings</TabsTrigger>
          <TabsTrigger value="transactions" className="rounded-full" data-testid="admin-tab-transactions">Transactions</TabsTrigger>
          <TabsTrigger value="users" className="rounded-full" data-testid="admin-tab-users">Users</TabsTrigger>
          <TabsTrigger value="taxes" className="rounded-full" data-testid="admin-tab-taxes">Taxes</TabsTrigger>
        </TabsList>
        <TabsContent value="complaints"><Complaints /></TabsContent>
        <TabsContent value="approvals"><Approvals /></TabsContent>
        <TabsContent value="bookings" className="overflow-x-auto rounded-3xl border bg-white"><Bookings /></TabsContent>
        <TabsContent value="transactions" className="space-y-6"><Link to="/admin/cpa-pack" className="inline-block rounded-full border px-4 py-2 text-sm font-semibold hover:bg-stone-50" data-testid="cpa-pack-link">Open CPA review pack</Link><AdminLedger /><AdminIncome /></TabsContent>
        <TabsContent value="users" className="overflow-x-auto rounded-3xl border bg-white"><Users /></TabsContent>
        <TabsContent value="taxes"><AdminTaxLiability /><TaxComplianceNotice /><TaxRules /></TabsContent>
      </Tabs>
    </div>
  );
}
